/* eslint-disable max-len */
import React, {useCallback, createElement, useContext, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {ReactComponent as alert} from '../../assets/icons/alerts/system-alerta-general.svg';
import {ReactComponent as arrow} from '../../assets/icons/buttons/systemArrowRight.svg';
import {useTranslation} from 'react-i18next';
import {isMobile} from 'react-device-detect';
import {useParams} from 'react-router-dom';
import ButtonSection from '../../components/buttons/button-section/button-section';
import {MAP_VIEW} from '../../core/constants';
import {DataContext} from '../../services/data-v2/data.context';
import {HeaderContext} from '../../services/header/header.context';
import {MapContext} from '../../services/map-v2/map.context';
import {AmbIcon} from '../amb-icon/amb-icon.component';
import {ItinerarySelectorComponent} from '../itinerary-selector/itinerary-selector.component';
import {NavOptionsComponent} from '../nav-options/nav-options.component';
import {LineDetailStopsComponent} from './line-detail-stops.component';
import {analyticsEvent} from '../../utils/analytics';

import './line-detail.scss';

interface TabInterface {
    id: number,
    translate: string,
    visible: boolean,
};

export const TABS = [
    {id: 1, translate: 'translation:transport_line_detail_tab_route', visible: true},
    {id: 2, translate: 'translation:transport_line_detail_tab_schedules', visible: true},
    {id: 3, translate: 'translation:web_nav_information', visible: false},
];

export const LineDetailComponent = ({buttons}: { buttons: string[] }) => {
    const {t} = useTranslation();
    const availableButtons = [...buttons];
    const {routeId, stopId} = useParams<any>();

    const {
        ready,
        getCurrentStop,
        currentStop,
        currentRoute,
        getCurrentRoute,
        currentTrip,
        setCurrentTrip,
        currentRouteAlerts,
        realTimeWorker,
    } = useContext(DataContext);
    const {setView} = useContext(MapContext);
    const {initHeader} = useContext(HeaderContext);

    if (currentRoute && currentRoute.mapUrl) {
        availableButtons.push('map');
    }


    const [selectedTab, setSelectedTab] = useState<TabInterface>(TABS[0]);

    const setHeader = useCallback(() => {
        let headerConfig = {};
        if (!isMobile && currentRoute) {
            headerConfig = {
                title: currentRoute?.longName || '',
                backIcon: true,
                backToPrevius: false,
                closeIcon: false,
                logoIcon: false,
                iconComponent: <AmbIcon
                    text={currentRoute.shortName}
                    color={currentRoute.textColor}
                    backgroundColor={currentRoute.color}
                    shape={currentRoute.slug === 'busamb' ? 'round' : 'square'}
                    size="m"/>,
                customClassName: {
                    'backgroundColor': 'var(--gris-fons-amb)',
                },
                packPath: currentStop ? `/stops/${currentStop.id}` : '/home',
            };
            initHeader(headerConfig);
        }
    }, [currentRoute]);

    const handleAlertClick = () => {
        analyticsEvent('Detall línia', 'linedetail_alteration_click');
    };

    const handleDownloadPDF = async (url: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error al descargar el archivo: ${response.statusText}`);
            }
            const blob = await response.blob();
            const link = document.createElement('a');
            const fileName = url.split('/').pop() || 'timetable.pdf';
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error descargando el archivo:', error);
        }
    };

    useEffect(() => {

        if (ready) getCurrentRoute(routeId);
        return () => {
            getCurrentRoute();
        };
    }, [routeId, ready]);

    useEffect(() => {

        if (ready) getCurrentStop(stopId);
        return () => {
            getCurrentStop();
        };
    }, [stopId, ready]);

    useEffect(() => {
        setHeader();
    }, [currentRoute]);

    useEffect(() => {
        setView(MAP_VIEW.ROUTE);
        if (window.location.pathname.includes('serveis-aprop')) {
            realTimeWorker?.startCloseStopsPolling?.();
        }
        return () => {
            setView(MAP_VIEW.STOPS);
            if (realTimeWorker?.stopCloseStopsPolling) {
                realTimeWorker.stopCloseStopsPolling();
            }
        };
    }, []);

    return <div className={`line-detail-container${isMobile ? ' mobile' : ''}`}>
        {
            isMobile && currentRoute && <div className="mobile-header">
                <AmbIcon
                    text={currentRoute.shortName}
                    color={currentRoute.textColor}
                    backgroundColor={currentRoute.color}
                    shape={currentRoute.slug === 'busamb' ? 'round' : 'square'}
                    size="m"/>
                <div>{currentRoute.longName}</div>
            </div>
        }

        {
            currentRouteAlerts &&
            currentRoute &&
            currentRouteAlerts.some((alert) => alert.alertRoute.includes(currentRoute.routeId)) ? (
                    <div
                        className="warning-alert"
                        onClick={handleAlertClick}
                        onKeyDown={handleAlertClick}
                        role="button"
                        tabIndex={0}
                    >
                        <Link to={`/alerts/lines/${currentRoute.id}`}>
                            <span className="alert-icon">
                                {createElement(alert)}
                                <p>{t('translation:transport_line_detail_new_alerts')}</p>
                            </span>
                            <span className="alert-arrow">{createElement(arrow)}</span>
                        </Link>
                    </div>
                ) : null
        }


        {
            currentRoute && <ButtonSection txtButtons={availableButtons} mapUrl={currentRoute.mapUrl} />
        }

        {
            currentRoute && <div className="pin_nav-tabs" style={{
                ...(isMobile && {'padding': '0 20px'}),
            }}>
                <NavOptionsComponent value={{
                    tabOptions: TABS,
                    changeOption: setSelectedTab,
                    selectedOption: selectedTab,
                }}/>
            </div>
        }

        {
            selectedTab.id === 1 && currentRoute && <>
                <ItinerarySelectorComponent
                    route={currentRoute}
                    itineraries={currentRoute.tripsPaths.map((trip, index) => ({name: trip.tripHeadsign, id: index}))}
                    selected={currentTrip}
                    onChange={setCurrentTrip}
                />

                <LineDetailStopsComponent/>
            </>

        }

        {
            selectedTab.id === 2 && currentRoute && <div className="time-table">
                {
                    currentRoute.services.map((service, key) => <div key={key}>
                        <div className="period">
                            {service.description}
                        </div>
                        <div className="times">
                            {service.timetableDescription}
                        </div>
                    </div>)
                }
                {
                    currentRoute && currentRoute.timetableUrl && (
                        <div className="download-button-container">
                            <button
                                onClick={() => handleDownloadPDF(currentRoute.timetableUrl as string)}
                                className="download-button"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg"
                                    height="24px"
                                    viewBox="0 -960 960 960"
                                    width="24px" fill="#000000">
                                    <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"/>
                                </svg>
                                {t('translation:transport_sheet_toolbar_map_schedule_pdf')}
                            </button>
                        </div>
                    )
                }
            </div>
        }
    </div>;
};
